import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import { InfoToggle, Select } from '../../../../types/redux/pages/PagesStore';
import {
  APPEND_NEW_INFOTOGGLE_TO_TITLEBAR,
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_INFOTOGGLE_FROM_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import FundAssetLiquidationTable, { FALTDisplayOptions } from './table';
import { useFundAssetLiquidation } from './queries';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';

export const LiquidityUrls = {
  FETCH_FUND_LIQUIDATION_TIME_COST: 'aggregate_liquidity_risk_report_data/2',
  FETCH_FUND_LIQUIDITY_RISK_OVERVIEW: 'aggregate_liquidity_risk_report_data/3',
  FETCH_EVOLUTION: 'aggregate_lqdty_review_data',
  FETCH_RST: 'historical_liquidity_reverse_stress_tests_agg',
};

const defaultDropDownValues = [
  { label: 'BAU', value: 'data' },
  { label: 'Stressed', value: 'data_s' },
  { label: 'Super-Stressed', value: 'data_ss' },
];

const FundAssetLiquidationTime: FC<FundInfoComponentProps> = ({
  positionDate,
  fundId,
}) => {
  const dispatch = useDispatch();

  const [scenariosOptions, setScenariosOptions] = useState<any>(null);

  const { data, error, isError, isLoading, isFetching } = useFundAssetLiquidation(
    fundId,
    positionDate,
  );

  const liquidationData = data?.data;

  useEffect(() => {
    dispatch(toggleTitleBarLoading(true));
    if (liquidationData?.liquidation_time_data_keys) {
      setScenariosOptions(
        liquidationData.liquidation_time_data_keys.map((item: any) => ({
          label: item[0],
          value: item[1],
        })),
      );
    } else {
      setScenariosOptions(defaultDropDownValues);
    }
  }, [liquidationData]);

  // Add the select.
  useEffect(() => {
    if (scenariosOptions) {
      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Select Scenario',
          titleBarKey: 'scenarioSelection',
          values: scenariosOptions,
          displayOnOverviewPage: false,
          aValueIsAlwaysSelected: true,
          currentValue: scenariosOptions[0].value,
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );

      const displaysToAdd: Select[] = [
        {
          titleBarTitle: 'Selected Display O/P',
          titleBarKey: 'displaySelection',
          values: [
            { label: 'Terminal', value: 'Scenario1' },
            { label: 'Periodical', value: 'Scenario2' },
            { label: 'Aggregate', value: 'Scenario3' },
          ],
          displayOnOverviewPage: false,
          aValueIsAlwaysSelected: true,
          currentValue: 'Scenario2',
        },
      ];

      dispatch(
        addAdditionalTitleBarComponents(
          displaysToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );

      const infoTogglesToAdd: InfoToggle[] = [
        {
          titleBarTitle: 'Select Scenario',
          titleBarKey: 'scenarioSelection',
          displayOnOverviewPage: false,
          currentValue: '',
          message: (
            <p>
              This page shows the liquidation time of the fund and each
              individual position based on a fraction of the average market
              volume.
              <br />
              The volume used for the calculation is defined below:
              <br />
              <br />
              BAU: 10% of the Average Market Volume
              <br />
              Stressed: 7.5% of the Average Market Volume
              <br />
              Super Stressed: 5% of the Average Market Volume
            </p>
          ),
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          infoTogglesToAdd,
          APPEND_NEW_INFOTOGGLE_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
    }

    return () => {
      dispatch(
        removeTitleBarComponent(
          'scenarioSelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
      dispatch(
        removeTitleBarComponent(
          'displaySelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
      dispatch(
        removeTitleBarComponent(
          'scenarioSelection',
          REMOVE_INFOTOGGLE_FROM_TITLEBAR,
        ),
      );
    };
  }, [scenariosOptions, dispatch]);

  const chosenStressLevel = useTitleBarSelect('scenarioSelection');

  const chosenDisplay = useTitleBarSelect(
    'displaySelection',
  ) as FALTDisplayOptions;

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={liquidationData}
      noDataMessage="No fund asset liquidation data available."
    >
      {(resolvedData) => (
        <FundAssetLiquidationTable
          activeFund={fundId}
          stressLevel={chosenStressLevel || 'standard'}
          chosenDisplay={chosenDisplay}
          fundAssetData={resolvedData}
          stressLevelOptions={scenariosOptions}
          isFetching={isFetching}
        />
      )}
    </QueryBoundary>
  );
};

export default FundAssetLiquidationTime;
